import type { MessageKey } from '../../i18n/en';

export type MapThemeId = 'SILVER' | 'NIGHT' | 'RETRO' | 'STANDARD';
export type TrayHeight = 'SMALL' | 'MEDIUM' | 'LARGE';
export type TrayView = 'LIST' | 'GRID';
export type SpeedUnit = 'kmh' | 'mph';
export type MarkerStyle = 'GOOGLE' | 'CAR' | 'WAZE';
export type NameFormatId =
  | 'DAY_SHORT_DATE_12H'
  | 'FULL_DAY_DATE_12H'
  | 'SHORT_DAY_DATE_YEAR_24H'
  | 'DAY_MONTH_YEAR_12H'
  | 'EU_DATE_24H'
  | 'US_DATE_12H'
  | 'ISO_DATE_24H'
  | 'TIME_24H_DAY_DATE'
  | 'MONTH_DAY_YEAR_24H'
  | 'DAY_DATE_TIME_12H_SEC';

export type DefaultCategoryId =
  | 'map'
  | 'stationary'
  | 'playlist'
  | 'itinerary'
  | 'joystick'
  | 'player';

export interface AppDefaults {
  mapTheme: MapThemeId;
  mapZoom: number;
  mapTilt: number;
  stationaryAccuracy: number;
  stationaryNameFormat: NameFormatId;
  stationarySearchKm: number;
  stationaryTray: TrayHeight;
  stationaryView: TrayView;
  playlistTimeSec: number;
  playlistAccuracy: number;
  playlistSearchKm: number;
  playlistTray: TrayHeight;
  playlistView: TrayView;
  itinerarySpeedKmh: number;
  itinerarySpeedUnit: SpeedUnit;
  itineraryAccuracy: number;
  itinerarySearchKm: number;
  itineraryColor: string;
  joystickSpeedUnit: SpeedUnit;
  joystickSpeedKmh: number;
  joystickAccuracy: number;
  joystickNameFormat: NameFormatId;
  joystickMarker: MarkerStyle;
  joystickPolyline: string;
  overlayStationary: boolean;
  overlayPlaylist: boolean;
  overlayItinerary: boolean;
  overlayJoystick: boolean;
}

export const ROUTE_COLORS = [
  '#1A73E8',
  '#EA4335',
  '#34A853',
  '#FB8C00',
  '#8E24AA',
  '#00897B',
  '#3949AB',
  '#C2185B',
  '#333333',
  '#252E3E',
  '#F07E62',
  '#F0B855',
  '#47A05E',
  '#00ACC1',
  '#6D4C41',
  '#5C6BC0',
] as const;

export const POLYLINE_COLORS = ['#7C3AED', ...ROUTE_COLORS] as const;

export const NAME_FORMATS: ReadonlyArray<{ id: NameFormatId; labelKey: MessageKey }> = [
  { id: 'DAY_SHORT_DATE_12H', labelKey: 'defaults.format.dayShort' },
  { id: 'FULL_DAY_DATE_12H', labelKey: 'defaults.format.fullDay' },
  { id: 'SHORT_DAY_DATE_YEAR_24H', labelKey: 'defaults.format.shortDayYear' },
  { id: 'DAY_MONTH_YEAR_12H', labelKey: 'defaults.format.dayMonthYear' },
  { id: 'EU_DATE_24H', labelKey: 'defaults.format.eu' },
  { id: 'US_DATE_12H', labelKey: 'defaults.format.us' },
  { id: 'ISO_DATE_24H', labelKey: 'defaults.format.iso' },
  { id: 'TIME_24H_DAY_DATE', labelKey: 'defaults.format.timeFirst' },
  { id: 'MONTH_DAY_YEAR_24H', labelKey: 'defaults.format.monthFirst' },
  { id: 'DAY_DATE_TIME_12H_SEC', labelKey: 'defaults.format.withSeconds' },
];

export const MAP_THEMES: ReadonlyArray<{
  id: MapThemeId;
  labelKey: MessageKey;
  descKey: MessageKey;
}> = [
  { id: 'SILVER', labelKey: 'defaults.theme.silver', descKey: 'defaults.theme.silverDesc' },
  { id: 'NIGHT', labelKey: 'defaults.theme.night', descKey: 'defaults.theme.nightDesc' },
  { id: 'RETRO', labelKey: 'defaults.theme.retro', descKey: 'defaults.theme.retroDesc' },
  { id: 'STANDARD', labelKey: 'defaults.theme.standard', descKey: 'defaults.theme.standardDesc' },
];

const KMH_TO_MPH = 0.621371192237;

export const DEFAULT_APP_DEFAULTS: AppDefaults = {
  mapTheme: 'SILVER',
  mapZoom: 14,
  mapTilt: 60,
  stationaryAccuracy: 25,
  stationaryNameFormat: 'DAY_SHORT_DATE_12H',
  stationarySearchKm: 50,
  stationaryTray: 'SMALL',
  stationaryView: 'LIST',
  playlistTimeSec: 8,
  playlistAccuracy: 15,
  playlistSearchKm: 50,
  playlistTray: 'MEDIUM',
  playlistView: 'LIST',
  itinerarySpeedKmh: 33,
  itinerarySpeedUnit: 'kmh',
  itineraryAccuracy: 28,
  itinerarySearchKm: 50,
  itineraryColor: '#333333',
  joystickSpeedUnit: 'kmh',
  joystickSpeedKmh: 10,
  joystickAccuracy: 5,
  joystickNameFormat: 'DAY_SHORT_DATE_12H',
  joystickMarker: 'GOOGLE',
  joystickPolyline: '#7C3AED',
  overlayStationary: true,
  overlayPlaylist: true,
  overlayItinerary: true,
  overlayJoystick: true,
};

const STORAGE_KEY = 'mockloc-app-defaults';

const CATEGORY_KEYS: Record<DefaultCategoryId, (keyof AppDefaults)[]> = {
  map: ['mapTheme', 'mapZoom', 'mapTilt'],
  stationary: [
    'stationaryAccuracy',
    'stationaryNameFormat',
    'stationarySearchKm',
    'stationaryTray',
    'stationaryView',
  ],
  playlist: [
    'playlistTimeSec',
    'playlistAccuracy',
    'playlistSearchKm',
    'playlistTray',
    'playlistView',
  ],
  itinerary: [
    'itinerarySpeedKmh',
    'itinerarySpeedUnit',
    'itineraryAccuracy',
    'itinerarySearchKm',
    'itineraryColor',
  ],
  joystick: [
    'joystickSpeedUnit',
    'joystickSpeedKmh',
    'joystickAccuracy',
    'joystickNameFormat',
    'joystickMarker',
    'joystickPolyline',
  ],
  player: ['overlayStationary', 'overlayPlaylist', 'overlayItinerary', 'overlayJoystick'],
};

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === 'number' ? value : Number.NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function clampFloat(value: unknown, min: number, max: number, fallback: number): number {
  const n = typeof value === 'number' ? value : Number.NaN;
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function hexOf(value: unknown, allowed: readonly string[], fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const found = allowed.find((item) => item.toLowerCase() === value.toLowerCase());
  return found ?? fallback;
}

function boolOf(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function sanitizeAppDefaults(value: Partial<AppDefaults> | null | undefined): AppDefaults {
  const raw = value ?? {};
  const base = DEFAULT_APP_DEFAULTS;
  return {
    mapTheme: oneOf(raw.mapTheme, ['SILVER', 'NIGHT', 'RETRO', 'STANDARD'] as const, base.mapTheme),
    mapZoom: clampInt(raw.mapZoom, 10, 20, base.mapZoom),
    mapTilt: clampInt(raw.mapTilt, 0, 65, base.mapTilt),
    stationaryAccuracy: clampInt(raw.stationaryAccuracy, 0, 300, base.stationaryAccuracy),
    stationaryNameFormat: oneOf(
      raw.stationaryNameFormat,
      NAME_FORMATS.map((item) => item.id),
      base.stationaryNameFormat
    ),
    stationarySearchKm: clampInt(raw.stationarySearchKm, 3, 50, base.stationarySearchKm),
    stationaryTray: oneOf(raw.stationaryTray, ['SMALL', 'MEDIUM', 'LARGE'] as const, base.stationaryTray),
    stationaryView: oneOf(raw.stationaryView, ['LIST', 'GRID'] as const, base.stationaryView),
    playlistTimeSec: clampInt(raw.playlistTimeSec, 0, 600, base.playlistTimeSec),
    playlistAccuracy: clampInt(raw.playlistAccuracy, 0, 500, base.playlistAccuracy),
    playlistSearchKm: clampInt(raw.playlistSearchKm, 3, 50, base.playlistSearchKm),
    playlistTray: oneOf(raw.playlistTray, ['SMALL', 'MEDIUM', 'LARGE'] as const, base.playlistTray),
    playlistView: oneOf(raw.playlistView, ['LIST', 'GRID'] as const, base.playlistView),
    itinerarySpeedKmh: clampInt(raw.itinerarySpeedKmh, 1, 300, base.itinerarySpeedKmh),
    itinerarySpeedUnit: oneOf(raw.itinerarySpeedUnit, ['kmh', 'mph'] as const, base.itinerarySpeedUnit),
    itineraryAccuracy: clampInt(raw.itineraryAccuracy, 1, 100, base.itineraryAccuracy),
    itinerarySearchKm: clampInt(raw.itinerarySearchKm, 3, 50, base.itinerarySearchKm),
    itineraryColor: hexOf(raw.itineraryColor, ROUTE_COLORS, base.itineraryColor),
    joystickSpeedUnit: oneOf(raw.joystickSpeedUnit, ['kmh', 'mph'] as const, base.joystickSpeedUnit),
    joystickSpeedKmh: clampFloat(raw.joystickSpeedKmh, 10, 400, base.joystickSpeedKmh),
    joystickAccuracy: clampInt(raw.joystickAccuracy, 1, 100, base.joystickAccuracy),
    joystickNameFormat: oneOf(
      raw.joystickNameFormat,
      NAME_FORMATS.map((item) => item.id),
      base.joystickNameFormat
    ),
    joystickMarker: oneOf(raw.joystickMarker, ['GOOGLE', 'CAR', 'WAZE'] as const, base.joystickMarker),
    joystickPolyline: hexOf(raw.joystickPolyline, POLYLINE_COLORS, base.joystickPolyline),
    overlayStationary: boolOf(raw.overlayStationary, base.overlayStationary),
    overlayPlaylist: boolOf(raw.overlayPlaylist, base.overlayPlaylist),
    overlayItinerary: boolOf(raw.overlayItinerary, base.overlayItinerary),
    overlayJoystick: boolOf(raw.overlayJoystick, base.overlayJoystick),
  };
}

export function readAppDefaults(): AppDefaults {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_APP_DEFAULTS;
    return sanitizeAppDefaults(JSON.parse(raw) as Partial<AppDefaults>);
  } catch {
    return DEFAULT_APP_DEFAULTS;
  }
}

export function storeAppDefaults(value: AppDefaults): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* Ignore storage failures. The in-memory choice still applies. */
  }
}

export function categoryKeys(category: DefaultCategoryId): (keyof AppDefaults)[] {
  return CATEGORY_KEYS[category];
}

export function isDefaultCategoryId(value: string): value is DefaultCategoryId {
  return Object.prototype.hasOwnProperty.call(CATEGORY_KEYS, value);
}

export function displaySpeed(kmh: number, unit: SpeedUnit): number {
  if (unit === 'mph') return Math.round(Math.max(0, kmh) * KMH_TO_MPH);
  return Math.round(kmh);
}

export function storageSpeedFromDisplay(display: number, unit: SpeedUnit): number {
  const kmh = unit === 'mph' ? display / KMH_TO_MPH : display;
  return Math.min(400, Math.max(10, kmh));
}

export function joystickDisplayBounds(unit: SpeedUnit): { min: number; max: number } {
  return {
    min: displaySpeed(10, unit),
    max: displaySpeed(400, unit),
  };
}

export function formatPointName(formatId: NameFormatId, date: Date, locale: string): string {
  const weekdayShort = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
  const weekdayLong = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
  const monthShort = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
  const monthLong = new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);
  const day = date.getDate();
  const day2 = String(day).padStart(2, '0');
  const month2 = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours();
  const hh24 = String(hours).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const hour12 = hours % 12 || 12;
  const hour12pad = String(hour12).padStart(2, '0');
  const period =
    new Intl.DateTimeFormat(locale, { hour: 'numeric', hour12: true })
      .formatToParts(date)
      .find((part) => part.type === 'dayPeriod')?.value ?? (hours < 12 ? 'AM' : 'PM');

  switch (formatId) {
    case 'FULL_DAY_DATE_12H':
      return `${weekdayLong}, ${monthLong} ${day}, ${year} at ${hour12}:${minutes} ${period}`;
    case 'SHORT_DAY_DATE_YEAR_24H':
      return `${weekdayShort}, ${day2} ${monthShort} ${year} ${hh24}:${minutes}`;
    case 'DAY_MONTH_YEAR_12H':
      return `${day} ${monthShort} ${year}, ${hour12}:${minutes} ${period}`;
    case 'EU_DATE_24H':
      return `${day2}/${month2}/${year} ${hh24}:${minutes}`;
    case 'US_DATE_12H':
      return `${month2}/${day2}/${year} ${hour12pad}:${minutes} ${period}`;
    case 'ISO_DATE_24H':
      return `${year}-${month2}-${day2} ${hh24}:${minutes}`;
    case 'TIME_24H_DAY_DATE':
      return `${hh24}:${minutes} | ${weekdayShort} ${day} ${monthShort} ${year}`;
    case 'MONTH_DAY_YEAR_24H':
      return `${monthShort} ${day}, ${year} at ${hh24}:${minutes}`;
    case 'DAY_DATE_TIME_12H_SEC':
      return `${weekdayShort}, ${day} ${monthShort} ${year}, ${hour12pad}:${minutes}:${seconds} ${period}`;
    case 'DAY_SHORT_DATE_12H':
    default:
      return `${weekdayShort} ${day} ${monthShort} | ${hour12}:${minutes} ${period}`;
  }
}
